import { useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DropSelect,
  SearchableSelect,
  Separator,
  Skeleton,
  toast,
  type OptionSelect,
} from '@zawarski/eti-ui';
import { useGetPalmettoGroupsQuery } from '@/services/palmettoGroupApi';
import {
  useGetAllActiveIncidentsQuery,
  useGetUserActiveIncidentsQuery,
} from '@/services/incidentsApi';
import {
  useGetSettingsQuery,
  useSaveSettingsMutation,
  type SettingsSave,
} from '@/services/settingsApi';
import { getGroupDisplayName } from '@/lib/utils';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  selectUserId,
  selectIsStateUser,
  selectActiveCountyId,
  selectIncidentGatheringId,
  selectIncidentViewingId,
  selectCanSetGathering,
  SET_INCIDENT_SETTINGS,
  SET_ACTIVE_COUNTY,
} from '@/store/auth';

export type SettingType = 'viewing' | 'gathering' | 'group';

interface ChangeSettingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  settingType: SettingType;
}

const CONFIG = {
  viewing: {
    title: 'Change Data Viewing Incident',
    description: 'Select the incident for viewing assessment data.',
    label: 'Data viewing incident',
    placeholder: 'Select an incident',
  },
  gathering: {
    title: 'Change Data Gathering Incident',
    description: 'Select the incident for gathering assessment data.',
    label: 'Data gathering incident',
    placeholder: 'Select an incident',
  },
  group: {
    title: 'Change County',
    description: 'Select the county for data gathering.',
    label: 'County',
    placeholder: 'Select a county',
  },
};

export function ChangeSettingModal({ open, onOpenChange, settingType }: ChangeSettingModalProps) {
  const dispatch = useAppDispatch();
  const userId = useAppSelector(selectUserId);
  const isStateUser = useAppSelector(selectIsStateUser);
  const activeCountyId = useAppSelector(selectActiveCountyId);
  const currentViewingId = useAppSelector(selectIncidentViewingId);
  const currentGatheringId = useAppSelector(selectIncidentGatheringId);

  const canSetGathering = useAppSelector(selectCanSetGathering);
  const isCountyDisabled = settingType === 'group' && !isStateUser;
  const isGatheringDisabled = settingType === 'gathering' && !canSetGathering;

  const [userValue, setUserValue] = useState<string | null>(null);
  const [saveSettings, { isLoading: saving }] = useSaveSettingsMutation();

  // Data queries
  const { data: palmettoGroups = [] } = useGetPalmettoGroupsQuery();
  const { data: incidentsData } = useGetAllActiveIncidentsQuery();
  const { data: userActiveIncidents } = useGetUserActiveIncidentsQuery();

  const {
    data: incidentGathering,
    isLoading: loadingGathering,
    isFetching: fetchingGathering,
  } = useGetSettingsQuery(
    { pvSettingType: 'incidentDataGatheringEDMA', pvGroupID: activeCountyId },
    { skip: !activeCountyId || settingType === 'viewing' }
  );

  const {
    data: incidentDataView,
    isLoading: loadingView,
    isFetching: fetchingView,
  } = useGetSettingsQuery(
    { pvSettingType: 'incidentDataViewingEDMA', pvAccountID: userId },
    { skip: !userId || settingType !== 'viewing' }
  );

  const isLoading =
    (settingType === 'viewing' && (loadingView || fetchingView)) ||
    (settingType === 'gathering' && (loadingGathering || fetchingGathering)) ||
    (settingType === 'group' && (loadingGathering || fetchingGathering));

  // Parse active incident IDs
  const activeIncidentIds = useMemo<number[]>(() => {
    if (!userActiveIncidents?.pvSettingValue) return [];
    try {
      return JSON.parse(userActiveIncidents.pvSettingValue) as number[];
    } catch {
      return [];
    }
  }, [userActiveIncidents]);

  // Options per type
  const incViewingOptions = useMemo<OptionSelect[]>(() => {
    const viewingId = incidentDataView?.[0]?.pvSettingValue
      ? Number(incidentDataView[0].pvSettingValue)
      : null;
    const allowedIds = viewingId ? [...activeIncidentIds, viewingId] : activeIncidentIds;
    return (incidentsData?.dataset || [])
      .filter((inc) => allowedIds.includes(inc.pvIncidentID))
      .map((inc) => ({
        label: inc.pvIncidentName,
        value: String(inc.pvIncidentID),
      }));
  }, [incidentsData, activeIncidentIds, incidentDataView]);

  const incGatheringOptions = useMemo<OptionSelect[]>(() => {
    const gatheringId = incidentGathering?.[0]?.pvSettingValue
      ? Number(incidentGathering[0].pvSettingValue)
      : null;
    const allowedIds = gatheringId ? [...activeIncidentIds, gatheringId] : activeIncidentIds;
    return (incidentsData?.dataset || [])
      .filter((inc) => allowedIds.includes(inc.pvIncidentID))
      .map((inc) => ({
        label: inc.pvIncidentName,
        value: String(inc.pvIncidentID),
      }));
  }, [incidentsData, activeIncidentIds, incidentGathering]);

  const groupOptions = useMemo<OptionSelect[]>(
    () =>
      palmettoGroups
        .filter((g) => g.pvGroupID === 1 || (g.pvGroupID >= 2 && g.pvGroupID <= 48 && g.pvGroupID !== 42))
        .map((g) => ({
          label: getGroupDisplayName(g),
          value: String(g.pvGroupID),
        })),
    [palmettoGroups]
  );

  const options =
    settingType === 'viewing'
      ? incViewingOptions
      : settingType === 'gathering'
        ? incGatheringOptions
        : groupOptions;

  const config = CONFIG[settingType];

  // Derive original value from Redux (source of truth post-save)
  const originalValue = useMemo(() => {
    if (settingType === 'viewing') {
      return currentViewingId ?? '';
    }
    if (settingType === 'gathering') {
      return currentGatheringId ?? '';
    }
    return activeCountyId ? String(activeCountyId) : '';
  }, [settingType, currentViewingId, currentGatheringId, activeCountyId]);

  // Display user override when set, otherwise fall back to original
  const value = userValue ?? originalValue;
  const hasChanged = userValue !== null && userValue !== originalValue;

  const handleSave = async () => {
    if (!value) return;
    try {
      if (settingType === 'viewing') {
        const prev = incidentDataView?.[0];
        const payload: SettingsSave = {
          pvSettingID: prev?.pvSettingID,
          pvSettingType: 'incidentDataViewingEDMA',
          pvAccountID: userId,
          pvSettingValue: value,
        };
        await saveSettings(payload).unwrap();
        dispatch(
          SET_INCIDENT_SETTINGS({
            gatheringId: currentGatheringId,
            viewingId: value,
          })
        );
      } else if (settingType === 'gathering') {
        const prev = incidentGathering?.[0];
        const payload: SettingsSave = {
          pvSettingID: prev?.pvSettingID,
          pvSettingType: 'incidentDataGatheringEDMA',
          pvSettingValue: value,
          pvGroupID: activeCountyId,
        };
        await saveSettings(payload).unwrap();
        dispatch(
          SET_INCIDENT_SETTINGS({
            gatheringId: value,
            viewingId: currentViewingId,
          })
        );
      } else {
        // County change: re-save gathering setting with new pvGroupID
        const prev = incidentGathering?.[0];
        if (prev) {
          const payload: SettingsSave = {
            pvSettingID: prev.pvSettingID,
            pvSettingType: 'incidentDataGatheringEDMA',
            pvSettingValue: prev.pvSettingValue,
            pvGroupID: Number(value),
          };
          await saveSettings(payload).unwrap();
        }
        // STATE users: persist county selection via activeGroupEDMA setting
        if (isStateUser) {
          const activeGroupPayload: SettingsSave = {
            pvSettingType: 'activeGroupEDMA',
            pvAccountID: userId,
            pvSettingValue: value,
          };
          await saveSettings(activeGroupPayload).unwrap();
        }
        dispatch(SET_ACTIVE_COUNTY(Number(value)));
      }
      toast.success('Setting saved');
      onOpenChange(false);
    } catch (error) {
      console.error(error);
      toast.error('Unable to save setting. Please try again.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={!saving ? onOpenChange : undefined}>
      <DialogContent className='max-w-[420px]! p-0!'>
        <DialogHeader className='items-start px-4 pt-4'>
          <DialogTitle>{config.title}</DialogTitle>
          <DialogDescription>{config.description}</DialogDescription>
        </DialogHeader>
        <Separator />
        <div className='p-4'>
          {isLoading ? (
            <Skeleton />
          ) : isGatheringDisabled ? (
            <div className='space-y-1'>
              <label className='text-sm font-medium'>{config.label}</label>
              <p className='text-muted-foreground text-sm'>
                {options.find((o) => o.value === value)?.label || 'Not set'}
              </p>
            </div>
          ) : settingType === 'group' ? (
            <DropSelect
              options={options}
              label={config.label}
              placeholder={config.placeholder}
              value={value}
              onChange={setUserValue}
              disabled={isCountyDisabled}
            />
          ) : (
            <SearchableSelect
              options={options}
              label={config.label}
              placeholder={config.placeholder}
              value={value}
              onChange={setUserValue}
            />
          )}
        </div>
        {!isLoading && (
          <>
            <Separator />
            <DialogFooter className='w-full! flex-row! justify-end gap-2 p-4'>
              <Button variant='outline' onClick={() => onOpenChange(false)} disabled={saving}>
                Cancel
              </Button>
              <Button loading={saving} disabled={!hasChanged || !value || isCountyDisabled || isGatheringDisabled} onClick={handleSave}>
                Save
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
